import React, { useState } from 'react';
import { Kid, Reward, Redemption, User } from '../types';
import { api } from '../api/client';

interface AdultZoneModalProps {
  isOpen: boolean;
  user: User | null;
  kids: Kid[];
  rewards: Reward[];
  redemptions: Redemption[];
  onClose: () => void;
  onCreateReward: (reward: {
    name: string;
    description?: string;
    emoji: string;
    star_cost: number;
    is_shared: boolean;
    kid_id?: string | null;
  }) => Promise<void>;
  onDeleteReward: (rewardId: string) => Promise<void>;
  onPinUpdated: () => void;
  onLogout: () => void;
}

const REWARD_EMOJIS = [
  '🍦', '🎬', '🧸', '🎢', '🍕', '🎮', '🚴', '🧁',
  '🎳', '🎯', '🏊', '🛹', '🏕️', '🎨', '🍿', '🎁',
];

export const AdultZoneModal: React.FC<AdultZoneModalProps> = ({
  isOpen,
  user,
  kids,
  rewards,
  redemptions,
  onClose,
  onCreateReward,
  onDeleteReward,
  onPinUpdated,
  onLogout,
}) => {
  if (!isOpen) return null;

  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinStep, setPinStep] = useState<'enter' | 'set' | 'confirm'>(
    user?.has_adult_pin ? 'enter' : 'set'
  );
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Adult Zone active tab
  const [activeTab, setActiveTab] = useState<'rewards' | 'history' | 'pin'>('rewards');

  // New Reward Form state
  const [rewardName, setRewardName] = useState('');
  const [rewardDesc, setRewardDesc] = useState('');
  const [rewardEmoji, setRewardEmoji] = useState('🍦');
  const [rewardCost, setRewardCost] = useState(300);
  const [isShared, setIsShared] = useState(false);
  const [targetKidId, setTargetKidId] = useState(kids[0]?.id || '');
  const [rewardSubmitting, setRewardSubmitting] = useState(false);
  const [rewardError, setRewardError] = useState('');

  // Keypad handler
  const handleKeypadPress = async (digit: string) => {
    setPinError('');
    const nextPin = pin + digit;
    if (nextPin.length > 4) return;
    setPin(nextPin);

    if (nextPin.length === 4) {
      if (pinStep === 'enter') {
        try {
          setVerifying(true);
          const res = await api.verifyPin(nextPin);
          if (res.valid) {
            setUnlocked(true);
            setPin('');
          } else {
            setPinError('Incorrect 4-digit PIN');
            setPin('');
          }
        } catch (err: any) {
          setPinError(err.message || 'Verification failed');
          setPin('');
        } finally {
          setVerifying(false);
        }
      } else if (pinStep === 'set') {
        setConfirmPin(nextPin);
        setPin('');
        setPinStep('confirm');
      } else if (pinStep === 'confirm') {
        if (nextPin === confirmPin) {
          try {
            setVerifying(true);
            await api.setPin(nextPin);
            onPinUpdated();
            setUnlocked(true);
            setPin('');
          } catch (err: any) {
            setPinError(err.message || 'Failed to save PIN');
            setPin('');
            setPinStep('set');
          } finally {
            setVerifying(false);
          }
        } else {
          setPinError('PINs do not match! Try again.');
          setPin('');
          setConfirmPin('');
          setPinStep('set');
        }
      }
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
  };

  const handleCreateReward = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rewardName.trim()) {
      setRewardError('Please enter a reward name');
      return;
    }

    try {
      setRewardSubmitting(true);
      setRewardError('');
      await onCreateReward({
        name: rewardName.trim(),
        description: rewardDesc.trim() || undefined,
        emoji: rewardEmoji,
        star_cost: Number(rewardCost) || 100,
        is_shared: isShared,
        kid_id: isShared ? null : targetKidId,
      });

      // Reset
      setRewardName('');
      setRewardDesc('');
      setRewardCost(300);
      setIsShared(false);
    } catch (err: any) {
      setRewardError(err.message || 'Failed to create reward');
    } finally {
      setRewardSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: unlocked ? '560px' : '420px' }}
      >
        <div className="modal-header">
          <h3 className="modal-title">
            {unlocked ? 'Adult Zone 👑' : 'Adult Zone 🔒'}
          </h3>
          <button
            id="btn-close-adult-zone"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {!unlocked ? (
          /* PIN Entry Screen */
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 800, color: '#1e1b4b', marginBottom: '6px' }}>
              {pinStep === 'enter' && 'Enter 4-Digit Adult PIN'}
              {pinStep === 'set' && 'Choose Your 4-Digit PIN'}
              {pinStep === 'confirm' && 'Confirm Your 4-Digit PIN'}
            </h4>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              {pinStep === 'enter' && 'Enter your PIN to access rewards and settings.'}
              {pinStep === 'set' && 'First time here? Pick any 4 digits to protect adult controls.'}
              {pinStep === 'confirm' && 'Re-enter your 4 digits to confirm.'}
            </p>

            {pinError && (
              <div
                style={{
                  padding: '10px 14px',
                  background: '#fee2e2',
                  color: '#dc2626',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 700,
                  marginBottom: '16px',
                }}
              >
                {pinError}
              </div>
            )}

            <div className="pin-display">
              {[0, 1, 2, 3].map((idx) => (
                <div
                  key={idx}
                  className={`pin-dot ${pin.length > idx ? 'filled' : ''}`}
                />
              ))}
            </div>

            <div className="pin-keypad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                <button
                  key={digit}
                  type="button"
                  id={`adult-keypad-${digit}`}
                  className="keypad-btn"
                  onClick={() => handleKeypadPress(digit)}
                  disabled={verifying}
                >
                  {digit}
                </button>
              ))}
              <button
                type="button"
                className="keypad-btn"
                onClick={() => setPin('')}
                style={{ fontSize: '14px', color: '#64748b' }}
              >
                Clear
              </button>
              <button
                type="button"
                id="adult-keypad-0"
                className="keypad-btn"
                onClick={() => handleKeypadPress('0')}
                disabled={verifying}
              >
                0
              </button>
              <button
                type="button"
                id="adult-keypad-backspace"
                className="keypad-btn"
                onClick={handleBackspace}
                disabled={verifying}
              >
                ⌫
              </button>
            </div>
          </div>
        ) : (
          /* Unlocked Adult Zone Dashboard */
          <div>
            <div className="adult-tabs">
              <button
                id="tab-adult-rewards"
                className={`adult-tab ${activeTab === 'rewards' ? 'active' : ''}`}
                onClick={() => setActiveTab('rewards')}
              >
                🎁 Manage Rewards
              </button>
              <button
                id="tab-adult-history"
                className={`adult-tab ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                📜 Redemption History ({redemptions.length})
              </button>
              <button
                id="tab-adult-pin"
                className={`adult-tab ${activeTab === 'pin' ? 'active' : ''}`}
                onClick={() => setActiveTab('pin')}
              >
                ⚙️ PIN & Family
              </button>
            </div>

            {activeTab === 'rewards' && (
              <div>
                <div
                  style={{
                    background: '#faf5ff',
                    border: '1.5px solid #ede9fe',
                    borderRadius: '20px',
                    padding: '18px',
                    marginBottom: '24px',
                  }}
                >
                  <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#1e1b4b', marginBottom: '14px' }}>
                    + Create New Reward
                  </h4>

                  {rewardError && (
                    <div style={{ color: '#ef4444', fontSize: '13px', fontWeight: 700, marginBottom: '10px' }}>
                      {rewardError}
                    </div>
                  )}

                  <form onSubmit={handleCreateReward}>
                    <div className="form-row">
                      <div>
                        <label className="form-label">Reward Name</label>
                        <input
                          id="input-reward-name"
                          className="form-input"
                          type="text"
                          placeholder="e.g. Leo's Ice Cream"
                          value={rewardName}
                          onChange={(e) => setRewardName(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">⭐ Star Cost</label>
                        <input
                          id="input-reward-cost"
                          className="form-input"
                          type="number"
                          min="1"
                          max="10000"
                          value={rewardCost}
                          onChange={(e) => setRewardCost(Number(e.target.value))}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Description (optional)</label>
                      <input
                        id="input-reward-desc"
                        className="form-input"
                        type="text"
                        placeholder="e.g. Any flavour you want!"
                        value={rewardDesc}
                        onChange={(e) => setRewardDesc(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Choose Emoji</label>
                      <div className="icon-grid">
                        {REWARD_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            className={`icon-option-btn ${rewardEmoji === emoji ? 'selected' : ''}`}
                            onClick={() => setRewardEmoji(emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-row">
                      <div>
                        <label className="form-label">Reward Scope</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            className={`pill-btn ${!isShared ? 'selected' : ''}`}
                            onClick={() => setIsShared(false)}
                          >
                            Per-Kid
                          </button>
                          <button
                            type="button"
                            className={`pill-btn ${isShared ? 'selected' : ''}`}
                            onClick={() => setIsShared(true)}
                          >
                            Shared (Family)
                          </button>
                        </div>
                      </div>

                      {!isShared && (
                        <div>
                          <label className="form-label">Assigned Kid</label>
                          <select
                            id="select-reward-kid"
                            className="form-input"
                            value={targetKidId}
                            onChange={(e) => setTargetKidId(e.target.value)}
                          >
                            {kids.map((k) => (
                              <option key={k.id} value={k.id}>
                                {k.avatar_emoji} {k.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <button
                      id="btn-submit-create-reward"
                      type="submit"
                      className="btn-primary-gradient"
                      disabled={rewardSubmitting}
                      style={{ marginTop: '8px' }}
                    >
                      {rewardSubmitting ? 'Creating...' : '+ Create Reward 🎁'}
                    </button>
                  </form>
                </div>

                {/* Existing Rewards List */}
                <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#1e1b4b', marginBottom: '12px' }}>
                  Current Rewards
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {rewards.map((r) => {
                    const targetKid = kids.find((k) => k.id === r.kid_id);
                    return (
                      <div
                        key={r.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          border: '1.5px solid #ede9fe',
                          borderRadius: '16px',
                          background: '#ffffff',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '26px' }}>{r.emoji}</span>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <strong style={{ fontSize: '15px', color: '#1e1b4b' }}>{r.name}</strong>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 800,
                                  padding: '2px 8px',
                                  borderRadius: '8px',
                                  background: r.is_shared === 1 ? '#ffedd5' : '#ede9fe',
                                  color: r.is_shared === 1 ? '#ea580c' : '#7c3aed',
                                }}
                              >
                                {r.is_shared === 1 ? 'Shared' : targetKid ? `${targetKid.name}'s` : 'Personal'}
                              </span>
                            </div>
                            <span style={{ fontSize: '13px', color: '#64748b' }}>
                              ⭐ {r.star_cost} stars {r.description ? `· ${r.description}` : ''}
                            </span>
                          </div>
                        </div>

                        <button
                          id={`btn-delete-reward-${r.id}`}
                          className="btn-delete-quest"
                          onClick={() => {
                            if (confirm(`Delete reward "${r.name}"?`)) {
                              onDeleteReward(r.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#1e1b4b', marginBottom: '12px' }}>
                  Redemption History
                </h4>
                {redemptions.length === 0 ? (
                  <div className="empty-state">
                    <p style={{ color: '#64748b' }}>No rewards have been redeemed yet.</p>
                  </div>
                ) : (
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Kid</th>
                        <th>Reward</th>
                        <th>Stars</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redemptions.map((item) => (
                        <tr key={item.id}>
                          <td>{new Date(item.redeemed_at).toLocaleDateString()}</td>
                          <td>{item.kid_name || (item.is_shared === 1 ? 'Family' : 'Kid')}</td>
                          <td>
                            <span>{item.reward_emoji} </span>
                            <span>{item.reward_name}</span>
                          </td>
                          <td>⭐ {item.star_cost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'pin' && (
              <div>
                <div style={{ background: '#f8fafc', borderRadius: '18px', padding: '18px', marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#1e1b4b', marginBottom: '6px' }}>
                    Reset or Change PIN
                  </h4>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
                    Set a new 4-digit PIN for Adult Zone and reward redemptions.
                  </p>
                  <button
                    className="pill-btn"
                    onClick={() => {
                      setUnlocked(false);
                      setPinStep('set');
                      setPin('');
                    }}
                  >
                    Change 4-Digit PIN
                  </button>
                </div>

                <div style={{ background: '#f8fafc', borderRadius: '18px', padding: '18px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#1e1b4b', marginBottom: '6px' }}>
                    Account Session
                  </h4>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
                    Logged in as <strong>{user?.name || user?.email || 'Demo Parent'}</strong>
                  </p>
                  <button
                    id="btn-adult-logout"
                    className="btn-delete-quest"
                    onClick={onLogout}
                  >
                    Sign Out / Switch Family
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
